/**
 * Auto-poll cap for referrals stuck in the SENDING state.
 *
 * A referral sits in SENDING for the entire send workflow, which is usually a
 * minute or two but can legitimately last hours or days — e.g. while the
 * workflow waits for a clinic fax number to be entered manually. Polling such a
 * referral every few seconds for days would hammer the (relatively expensive)
 * referrals-list query for no benefit, so we stop auto-refreshing once a
 * referral has been SENDING for longer than this cap. The page still reflects
 * the latest state on manual refresh or navigation.
 *
 * Note: TanStack Query's `refetchIntervalInBackground` defaults to false, so
 * polling already pauses while the tab is backgrounded. This cap covers the
 * other case — a focused tab left open on a stuck SENDING referral.
 */
export const SENDING_POLL_CAP_MS = 60 * 60_000;

/**
 * Cap for polling a document whose thumbnail is still rendering. Thumbnails are
 * generated asynchronously off the request path (in the render Lambda) and are
 * normally ready within seconds; if one hasn't appeared in this window it has
 * almost certainly failed, so we stop polling and show the placeholder.
 */
export const THUMBNAIL_POLL_CAP_MS = 3 * 60_000;

/**
 * Window for polling an order request that is still sending. It normally ends
 * in a minute or two, but the row can stay PROCESSING for hours while a busy
 * line is retried, so the poll is gated on the fax's own start time: an old
 * unfinished fax never polls.
 */
export const ORDER_REQUEST_POLL_WINDOW_MS = 10 * 60_000;

export function orderRequestRefetchInterval(
  fax: { status: string; createdAt: string } | null | undefined,
  intervalMs: number
): number | false {
  if (fax?.status !== 'PENDING' && fax?.status !== 'PROCESSING') return false;
  const age = Date.now() - new Date(fax.createdAt).getTime();
  return age < ORDER_REQUEST_POLL_WINDOW_MS ? intervalMs : false;
}

/**
 * Computes a `refetchInterval`: poll at `intervalMs` while `active`, but stop
 * once `capMs` has elapsed since polling began. `startRef` holds that start
 * timestamp and is reset whenever `active` goes false so it re-arms next time.
 * Count polls (notification / unlinked-fax badges, message threads) pass
 * their query's `enabled`: TanStack calls `refetchInterval` even while a
 * query is disabled, so passing `true` would start the window too early.
 */
export function cappedRefetchInterval(
  active: boolean,
  startRef: { current: number | null },
  intervalMs: number,
  capMs: number
): number | false {
  if (!active) {
    startRef.current = null;
    return false;
  }
  if (startRef.current === null) {
    startRef.current = Date.now();
  }
  return Date.now() - startRef.current < capMs ? intervalMs : false;
}

/** Poll while a referral is SENDING, capped by {@link SENDING_POLL_CAP_MS}. */
export function sendingRefetchInterval(
  isSending: boolean,
  startRef: { current: number | null },
  intervalMs: number
): number | false {
  return cappedRefetchInterval(
    isSending,
    startRef,
    intervalMs,
    SENDING_POLL_CAP_MS
  );
}

/**
 * Poll while any document thumbnail is still PENDING, capped by
 * {@link THUMBNAIL_POLL_CAP_MS}.
 */
export function thumbnailRefetchInterval(
  anyPending: boolean,
  startRef: { current: number | null },
  intervalMs: number
): number | false {
  return cappedRefetchInterval(
    anyPending,
    startRef,
    intervalMs,
    THUMBNAIL_POLL_CAP_MS
  );
}
